import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  Upload,
  CheckCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Info,
  RotateCcw,
  X,
  Loader,
  Package,
  PlayCircle,
  Trash2,
  Users,
  RefreshCw,
  Plug,
  FileArchive,
  Settings,
} from 'lucide-react';
import { useHistory } from 'react-router-dom';
import ScormService, { ScormCourse, ScormRegistrationRow } from '../../_services/scorm.service';
import '../data-ingestion/data-ingestion.css';
import './scorm-courses.css';

type View = 'library' | 'upload';
type UploadStep = 1 | 2 | 3 | 4;

const UPLOAD_STEP_LABELS = ['Instructions', 'Upload Package', 'Importing', 'Result'];

const extractError = (err: any): string => {
  const data = err?.response?.data;
  if (typeof data?.error === 'string') return data.error;
  if (data?.error?.message) return data.error.message;
  return err?.message || 'Something went wrong';
};

const formatSize = (bytes: number | null): string => {
  if (!bytes) return '-';
  if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
};

const formatDate = (value: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatusChip: React.FC<{ course: ScormCourse }> = ({ course }) => {
  const status = course.importStatus || 'UNKNOWN';
  if (status === 'READY') {
    return <span className="di-status-badge di-status-valid"><CheckCircle size={14} /> Ready</span>;
  }
  if (status === 'ERROR') {
    return (
      <span className="di-status-badge di-status-error" title={course.importError || undefined}>
        <AlertTriangle size={14} /> Import failed
      </span>
    );
  }
  if (status === 'UPLOADING' || status === 'IMPORTING') {
    return <span className="di-status-badge scorm-status-importing"><Loader size={14} className="di-spin" /> Importing…</span>;
  }
  return <span className="di-status-badge">{status}</span>;
};

const ScormCoursesPage: React.FC = () => {
  const history = useHistory();
  const [view, setView] = useState<View>('library');

  // Library state
  const [courses, setCourses] = useState<ScormCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [connection, setConnection] = useState<{ ok: boolean; detail: string } | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [launchingId, setLaunchingId] = useState<number | null>(null);

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<ScormCourse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Registrations modal
  const [regTarget, setRegTarget] = useState<ScormCourse | null>(null);
  const [registrations, setRegistrations] = useState<ScormRegistrationRow[]>([]);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState<string | null>(null);

  // Reset-progress confirmation (per learner, inside the registrations modal)
  const [resetTarget, setResetTarget] = useState<ScormRegistrationRow | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Upload wizard state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadStep, setUploadStep] = useState<UploadStep>(1);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [courseName, setCourseName] = useState('');
  const [courseDescription, setCourseDescription] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState<{ ok: boolean; message: string } | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadCourses = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await ScormService.listCourses();
      setCourses(res.data || []);
      setLibraryError(null);
    } catch (err: any) {
      setLibraryError(extractError(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  // Keep polling the library while any course is still importing
  useEffect(() => {
    const anyImporting = courses.some(
      (c) => c.importStatus === 'IMPORTING' || c.importStatus === 'UPLOADING',
    );
    if (!anyImporting || view !== 'library') return;
    const timer = setTimeout(async () => {
      for (const c of courses) {
        if (c.importStatus === 'IMPORTING' || c.importStatus === 'UPLOADING') {
          try {
            await ScormService.getImportStatus(c.id);
          } catch {
            /* status persists server-side; list reload below surfaces it */
          }
        }
      }
      loadCourses(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [courses, view, loadCourses]);

  useEffect(() => () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
  }, []);

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnection(null);
    try {
      const res = await ScormService.testConnection();
      setConnection({
        ok: !!res.data?.ok,
        detail: res.data?.ok
          ? `Connected to SCORM Cloud (app ${res.data.appId})`
          : 'SCORM Cloud did not respond to ping',
      });
    } catch (err: any) {
      setConnection({ ok: false, detail: extractError(err) });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleLaunch = async (course: ScormCourse) => {
    setLaunchingId(course.id);
    try {
      const res = await ScormService.launchCourse(course.id);
      if (res.data?.url) window.open(res.data.url, '_blank', 'noopener');
    } catch (err: any) {
      setLibraryError(`Launch failed: ${extractError(err)}`);
    } finally {
      setLaunchingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const inUse = deleteTarget.usedInModules > 0 || deleteTarget.issuedToBatches > 0;
      await ScormService.deleteCourse(deleteTarget.id, inUse);
      setDeleteTarget(null);
      loadCourses(true);
    } catch (err: any) {
      setDeleteError(extractError(err));
    } finally {
      setDeleting(false);
    }
  };

  const openRegistrations = async (course: ScormCourse, refresh = false) => {
    setRegTarget(course);
    setRegLoading(true);
    setRegError(null);
    try {
      const res = await ScormService.getRegistrations(course.id, refresh);
      setRegistrations(res.data || []);
    } catch (err: any) {
      setRegError(extractError(err));
    } finally {
      setRegLoading(false);
    }
  };

  const handleResetRegistration = async () => {
    if (!regTarget || !resetTarget?.userId) return;
    setResetting(true);
    setResetError(null);
    try {
      await ScormService.resetRegistration(regTarget.id, resetTarget.userId);
      setResetTarget(null);
      // Reflect the cleared progress in the list (no remote sync needed — the
      // reset already nulled the local columns server-side).
      await openRegistrations(regTarget, false);
    } catch (err: any) {
      setResetError(extractError(err));
    } finally {
      setResetting(false);
    }
  };

  // --- Upload wizard ---

  const resetWizard = () => {
    setUploadStep(1);
    setSelectedFile(null);
    setCourseName('');
    setCourseDescription('');
    setUploadError(null);
    setUploading(false);
    setImportResult(null);
    if (pollTimer.current) clearTimeout(pollTimer.current);
  };

  const acceptFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setUploadError('SCORM packages must be a .zip file (containing imsmanifest.xml at its root)');
      return;
    }
    setUploadError(null);
    setSelectedFile(file);
    if (!courseName.trim()) {
      setCourseName(file.name.replace(/\.zip$/i, '').replace(/[_-]+/g, ' '));
    }
  };

  const pollImport = (id: number, attempt = 0) => {
    pollTimer.current = setTimeout(async () => {
      try {
        const res = await ScormService.getImportStatus(id);
        const status = res.data?.importStatus;
        if (status === 'READY') {
          setImportResult({ ok: true, message: `"${res.data?.name}" imported successfully and is ready to launch.` });
          setUploadStep(4);
          loadCourses(true);
          return;
        }
        if (status === 'ERROR') {
          setImportResult({ ok: false, message: res.data?.importError || 'SCORM Cloud reported an import error.' });
          setUploadStep(4);
          loadCourses(true);
          return;
        }
        if (attempt >= 100) {
          setImportResult({ ok: false, message: 'Import is taking unusually long. Check the library — it will finish in the background.' });
          setUploadStep(4);
          return;
        }
        pollImport(id, attempt + 1);
      } catch (err: any) {
        setImportResult({ ok: false, message: extractError(err) });
        setUploadStep(4);
      }
    }, 3000);
  };

  const handleUpload = async () => {
    if (!selectedFile || !courseName.trim() || uploading) return;
    setUploading(true);
    setUploadError(null);
    try {
      const res = await ScormService.uploadCourse(selectedFile, courseName.trim(), courseDescription.trim());
      setUploadStep(3);
      pollImport(res.data.id);
    } catch (err: any) {
      setUploadError(extractError(err));
    } finally {
      setUploading(false);
    }
  };

  // --- Renderers ---

  const renderLibrary = () => (
    <>
      <div className="di-header scorm-header-row">
        <div>
          <h1>SCORM Course Library</h1>
          <p>Upload, manage and track SCORM / xAPI / AICC packages delivered through SCORM Cloud</p>
        </div>
        <div className="scorm-header-actions">
          <button
            className="di-btn-secondary scorm-settings-btn"
            onClick={() => history.push('/tenant-preferences?tab=scorm')}
            title="Edit SCORM Cloud connection settings"
            aria-label="SCORM Cloud settings"
          >
            <Settings size={18} />
          </button>
          <button className="di-btn-secondary" onClick={handleTestConnection} disabled={testingConnection}>
            {testingConnection ? <Loader size={18} className="di-spin" /> : <Plug size={18} />} Test Connection
          </button>
          <button className="di-btn-primary" onClick={() => { resetWizard(); setView('upload'); }}>
            <Upload size={18} /> Upload New Course
          </button>
        </div>
      </div>

      {connection && (
        <div className={connection.ok ? 'scorm-conn-banner ok' : 'di-error-banner'}>
          {connection.ok ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
          <span>{connection.detail}</span>
          <button className="di-error-close" onClick={() => setConnection(null)}><X size={16} /></button>
        </div>
      )}

      {libraryError && (
        <div className="di-error-banner">
          <AlertTriangle size={18} /><span>{libraryError}</span>
          <button className="di-error-close" onClick={() => setLibraryError(null)}><X size={16} /></button>
        </div>
      )}

      <div className="di-content-card">
        {loading ? (
          <div className="scorm-empty"><Loader size={28} className="di-spin" /><p>Loading SCORM courses…</p></div>
        ) : courses.length === 0 ? (
          <div className="scorm-empty">
            <Package size={44} />
            <h3>No SCORM courses yet</h3>
            <p>Upload a SCORM 1.2 / 2004, xAPI or AICC package to make it available inside learning modules.</p>
            <button className="di-btn-primary" onClick={() => { resetWizard(); setView('upload'); }}>
              <Upload size={18} /> Upload your first course
            </button>
          </div>
        ) : (
          <div className="di-table-wrapper">
            <table className="di-preview-table scorm-table">
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Status</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th>Usage</th>
                  <th>Learners</th>
                  <th>Completion</th>
                  <th className="scorm-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((c) => (
                  <tr key={c.id}>
                    <td className="scorm-td-name">
                      <div className="scorm-course-name">
                        {c.name} <span className="scorm-resource-id" title="Study resource Id">#{c.id}</span>
                      </div>
                      <div className="scorm-course-file" title={c.fileName || undefined}>
                        <FileArchive size={12} /> {c.fileName || '-'}{c.version ? ` · v${c.version}` : ''}
                      </div>
                    </td>
                    <td><StatusChip course={c} /></td>
                    <td>{formatSize(c.fileSizeInByte)}</td>
                    <td>
                      <div>{formatDate(c.createdOn)}</div>
                      <div className="scorm-muted">{c.createdBy || ''}</div>
                    </td>
                    <td>
                      <div className="scorm-muted">{c.usedInModules} module{c.usedInModules === 1 ? '' : 's'}</div>
                      <div className="scorm-muted">{c.issuedToBatches} batch issue{c.issuedToBatches === 1 ? '' : 's'}</div>
                    </td>
                    <td>{c.learners}</td>
                    <td>
                      {c.learners > 0 ? (
                        <>
                          <div>{c.completions}/{c.learners} completed</div>
                          {c.avgScore != null && <div className="scorm-muted">avg score {c.avgScore}</div>}
                        </>
                      ) : (
                        <span className="scorm-muted">—</span>
                      )}
                    </td>
                    <td className="scorm-td-actions">
                      <button
                        className="scorm-action-btn"
                        title="Preview / launch as yourself"
                        disabled={c.importStatus !== 'READY' || launchingId === c.id}
                        onClick={() => handleLaunch(c)}
                      >
                        {launchingId === c.id ? <Loader size={16} className="di-spin" /> : <PlayCircle size={16} />}
                      </button>
                      <button
                        className="scorm-action-btn"
                        title="Learner progress"
                        onClick={() => openRegistrations(c)}
                      >
                        <Users size={16} />
                      </button>
                      <button
                        className="scorm-action-btn scorm-action-danger"
                        title="Delete course"
                        onClick={() => { setDeleteError(null); setDeleteTarget(c); }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  const renderWizardInstructions = () => (
    <div className="di-instructions">
      <div className="di-info-banner">
        <Info size={20} />
        <span>
          Upload a SCORM-compliant ZIP package. It is imported into SCORM Cloud and becomes available
          as a study resource (type &ldquo;Scorm Course&rdquo;) that can be attached to learning modules.
        </span>
      </div>
      <div className="di-tips">
        <h3>Package requirements</h3>
        <ul>
          <li>A single <strong>.zip</strong> file with <code>imsmanifest.xml</code> at the root (SCORM 1.2 / SCORM 2004), or an xAPI / AICC package.</li>
          <li>Maximum size <strong>500 MB</strong>.</li>
          <li>Import runs on SCORM Cloud and can take from a few seconds to a couple of minutes for large packages — you can leave the page, the import continues in the background.</li>
          <li>Each learner gets an individual registration on first launch, so completion, score and time are tracked per learner.</li>
          <li>No package handy? Use <strong>Download Sample Package</strong> below — a small "Workplace Fire Safety Basics" course (SCORM 1.2) that reports completion and a score, ideal for demos.</li>
        </ul>
      </div>
      <div className="di-actions">
        <button className="di-btn-secondary" onClick={() => { resetWizard(); setView('library'); }}>
          <ArrowLeft size={18} /> Back to Library
        </button>
        <a
          className="di-btn-secondary"
          href={`${process.env.PUBLIC_URL || ''}/samples/sample-scorm-fire-safety.zip`}
          download="sample-scorm-fire-safety.zip"
          style={{ textDecoration: 'none' }}
        >
          <FileArchive size={18} /> Download Sample Package
        </a>
        <button className="di-btn-primary" onClick={() => setUploadStep(2)}>
          Next: Upload Package <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );

  const renderWizardUpload = () => (
    <div className="di-upload">
      <div
        className={`di-dropzone ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
        onDrop={(e) => {
          e.preventDefault(); e.stopPropagation(); setDragActive(false);
          const file = e.dataTransfer.files?.[0];
          if (file) acceptFile(file);
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) acceptFile(file);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
        />
        <div className="di-dropzone-icon">
          {selectedFile ? <FileArchive size={48} /> : <Upload size={48} />}
        </div>
        <div className="di-dropzone-text">
          {selectedFile ? (
            <><strong>{selectedFile.name}</strong><p>{formatSize(selectedFile.size)} · Click or drop to replace</p></>
          ) : (
            <><strong>Drag &amp; drop your SCORM package here</strong><p>or click to browse. Accepted format: .zip</p></>
          )}
        </div>
      </div>

      <div className="scorm-form">
        <label className="scorm-form-label">
          Course Name <span className="scorm-required">*</span>
          <input
            className="scorm-form-input"
            type="text"
            value={courseName}
            maxLength={255}
            placeholder="e.g. UK GDPR & Data Protection Essentials"
            onChange={(e) => setCourseName(e.target.value)}
          />
        </label>
        <label className="scorm-form-label">
          Description
          <textarea
            className="scorm-form-input scorm-form-textarea"
            value={courseDescription}
            maxLength={2000}
            placeholder="Optional — shown to admins in the library"
            onChange={(e) => setCourseDescription(e.target.value)}
          />
        </label>
      </div>

      {uploadError && (
        <div className="di-error-banner">
          <AlertTriangle size={18} /><span>{uploadError}</span>
          <button className="di-error-close" onClick={() => setUploadError(null)}><X size={16} /></button>
        </div>
      )}

      <div className="di-actions">
        <button className="di-btn-secondary" onClick={() => setUploadStep(1)}>
          <ArrowLeft size={18} /> Back
        </button>
        <button
          className="di-btn-primary"
          disabled={!selectedFile || !courseName.trim() || uploading}
          onClick={handleUpload}
        >
          {uploading
            ? (<><Loader size={18} className="di-spin" /> Uploading…</>)
            : (<>Upload &amp; Import <ArrowRight size={18} /></>)}
        </button>
      </div>
    </div>
  );

  const renderWizardImporting = () => (
    <div className="di-submitting">
      <div className="di-progress-container">
        <div className="di-progress-icon"><Package size={40} className="di-spin" /></div>
        <h3>Importing on SCORM Cloud…</h3>
        <p className="di-progress-text">
          Parsing the manifest and provisioning the course. This usually takes under a minute.
        </p>
      </div>
    </div>
  );

  const renderWizardResult = () => (
    <div className="di-result">
      {importResult?.ok ? (
        <div className="di-result-success">
          <div className="di-result-icon di-result-icon-success"><CheckCircle size={48} /></div>
          <h2>Import Successful</h2>
          <p>{importResult.message}</p>
        </div>
      ) : (
        <div className="di-result-error">
          <div className="di-result-icon di-result-icon-error"><AlertTriangle size={48} /></div>
          <h2>Import Failed</h2>
          <p>{importResult?.message || 'Something went wrong during the import.'}</p>
        </div>
      )}
      <div className="di-actions di-actions-center">
        <button className="di-btn-secondary" onClick={resetWizard}>
          <RotateCcw size={18} /> Upload Another
        </button>
        <button className="di-btn-primary" onClick={() => { resetWizard(); setView('library'); }}>
          Go to Library <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );

  const renderWizard = () => (
    <>
      <div className="di-header">
        <h1>Upload SCORM Course</h1>
        <p>Import a SCORM / xAPI / AICC package into the course library</p>
      </div>
      <div className="di-stepper">
        {UPLOAD_STEP_LABELS.map((label, idx) => {
          const stepNum = (idx + 1) as UploadStep;
          const isActive = stepNum === uploadStep;
          const isCompleted = stepNum < uploadStep;
          return (
            <React.Fragment key={stepNum}>
              <div className={`di-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                <div className="di-step-circle">{isCompleted ? <CheckCircle size={16} /> : stepNum}</div>
                <span className="di-step-label">{label}</span>
              </div>
              {idx < UPLOAD_STEP_LABELS.length - 1 && (
                <div className={`di-step-connector ${isCompleted ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="di-content-card">
        {uploadStep === 1 && renderWizardInstructions()}
        {uploadStep === 2 && renderWizardUpload()}
        {uploadStep === 3 && renderWizardImporting()}
        {uploadStep === 4 && renderWizardResult()}
      </div>
    </>
  );

  return (
    <div className="data-ingestion scorm-library">
      {view === 'library' ? renderLibrary() : renderWizard()}

      {/* Delete confirmation */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered dialogClassName="scorm-modal">
        <Modal.Header closeButton>
          <Modal.Title>Delete SCORM Course</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget && (
            <>
              <p>
                Delete <strong>{deleteTarget.name}</strong>? This removes the course from SCORM Cloud
                and deletes all {deleteTarget.learners} learner registration record{deleteTarget.learners === 1 ? '' : 's'}.
              </p>
              {(deleteTarget.usedInModules > 0 || deleteTarget.issuedToBatches > 0) && (
                <div className="di-error-banner" style={{ marginTop: 8 }}>
                  <AlertTriangle size={18} />
                  <span>
                    This course is referenced by {deleteTarget.usedInModules} learning module(s) and{' '}
                    {deleteTarget.issuedToBatches} batch issue(s). Those references will break.
                  </span>
                </div>
              )}
              {deleteError && (
                <div className="di-error-banner" style={{ marginTop: 8 }}>
                  <AlertTriangle size={18} /><span>{deleteError}</span>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete Course'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Learner progress */}
      <Modal show={!!regTarget} onHide={() => setRegTarget(null)} centered size="xl" dialogClassName="scorm-modal">
        <Modal.Header closeButton>
          <Modal.Title>Learner Progress{regTarget ? ` — ${regTarget.name}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="scorm-reg-toolbar">
            <span className="scorm-muted">
              {registrations.length} learner{registrations.length === 1 ? '' : 's'} · progress synced from SCORM Cloud
            </span>
            <Button
              variant="primary"
              size="sm"
              disabled={regLoading}
              onClick={() => regTarget && openRegistrations(regTarget, true)}
            >
              <RefreshCw size={14} style={{ marginRight: 6 }} />
              {regLoading ? 'Syncing…' : 'Sync from SCORM Cloud'}
            </Button>
          </div>
          {regError && (
            <div className="di-error-banner"><AlertTriangle size={18} /><span>{regError}</span></div>
          )}
          {regLoading ? (
            <div className="scorm-empty"><Loader size={24} className="di-spin" /><p>Loading…</p></div>
          ) : registrations.length === 0 ? (
            <p className="scorm-muted" style={{ padding: '12px 0' }}>No learners have launched this course yet.</p>
          ) : (
            <div className="di-table-wrapper">
              <table className="di-preview-table">
                <thead>
                  <tr>
                    <th>Learner</th>
                    <th>Completion</th>
                    <th>Result</th>
                    <th>Score</th>
                    <th>Time Spent</th>
                    <th>Last Launched</th>
                    <th>Last Synced</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((r) => (
                    <tr key={r.id}>
                      <td>{r.learnerName || `User #${r.userId}`}</td>
                      <td>
                        {r.completionStatus === 'COMPLETED'
                          ? <span className="di-status-badge di-status-valid"><CheckCircle size={14} /> Completed</span>
                          : <span className="di-status-badge scorm-status-importing">{r.completionStatus || 'Not synced'}</span>}
                      </td>
                      <td>{r.successStatus || '-'}</td>
                      <td>{r.score != null ? r.score : '-'}</td>
                      <td>{r.totalSecondsTracked != null ? `${Math.round(r.totalSecondsTracked / 60)} min` : '-'}</td>
                      <td>{formatDate(r.lastLaunchedOn)}</td>
                      <td>{formatDate(r.lastSyncedOn)}</td>
                      <td>
                        {r.userId ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => { setResetError(null); setResetTarget(r); }}
                            title="Reset this learner's progress so they can take the course again from the start"
                          >
                            <RotateCcw size={14} style={{ marginRight: 6 }} /> Reset
                          </Button>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Reset learner progress confirmation */}
      <Modal
        show={!!resetTarget}
        onHide={() => { if (!resetting) setResetTarget(null); }}
        centered
        dialogClassName="scorm-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Reset Learner Progress</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Reset <strong>{resetTarget?.learnerName || `User #${resetTarget?.userId}`}</strong>’s progress
            {regTarget ? <> on <strong>{regTarget.name}</strong></> : null}? This clears their completion,
            result, score and time-spent so they can take the course again from the start.
          </p>
          <p className="scorm-muted" style={{ marginBottom: 0 }}>
            The reset happens in place — the learner keeps their SCORM Cloud registration, so no new
            SCORM Cloud license is consumed.
          </p>
          {resetError && (
            <div className="di-error-banner" style={{ marginTop: 12 }}>
              <AlertTriangle size={18} /><span>{resetError}</span>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setResetTarget(null)} disabled={resetting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleResetRegistration} disabled={resetting}>
            {resetting ? 'Resetting…' : 'Reset Progress'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ScormCoursesPage;
